/**
 * KAN-829: блок "Telegram" в карточке менеджера. Менеджер привязывает свой
 * Telegram к боту платформы по одноразовой ссылке и получает туда
 * уведомления. Бот платформы не подключён (KAN-830): подсказка обратиться в поддержку. Не привязан:
 * "Получить ссылку", ссылка и "Копировать". Привязан: дата и "Отключить".
 * Менеджер в архиве: ссылку не выдаём (isActive=false).
 * Виден владельцу и оператору (canSetSeniorManager); остальным блока нет и
 * запросов нет. Данные и действия в хуках useManagerBot.
 */
import { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { copyToClipboard, useManagerBotStatus, useManagerTelegramLink } from '@/hooks/useManagerBot';
import { canSetSeniorManager } from '@/lib/managerAccess';

export const MANAGER_BOT_NOT_CONNECTED_HINT =
  'Бот для менеджеров ещё не подключён на платформе. Обратитесь в поддержку.';
export const MANAGER_LINK_HINT =
  'Ссылка действует 24 часа, откройте её в Telegram на телефоне менеджера и нажмите Start';

export const MANAGER_ARCHIVED_HINT = 'Менеджер в архиве, ссылку для подключения Telegram выдать нельзя';

interface Props {
  managerId: string;
  telegramLinked: boolean;
  telegramLinkedAt: string | null;
  /** false: менеджер в архиве, новую ссылку привязки не выдаём. */
  isActive?: boolean;
}

function formatLinkedAt(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('ru-RU');
}

export function ManagerTelegramBlock({
  managerId,
  telegramLinked,
  telegramLinkedAt,
  isActive = true,
}: Props): JSX.Element | null {
  const { data: user } = useCurrentUser();
  const allowed = canSetSeniorManager(user);
  const { data: status } = useManagerBotStatus(allowed);
  const { createLink, unlink, error } = useManagerTelegramLink(managerId);
  const [copied, setCopied] = useState(false);
  if (!allowed) return null;

  const linkUrl = createLink.data?.url;
  const handleCopy = async () => {
    if (linkUrl) setCopied(await copyToClipboard(linkUrl));
  };

  let body: JSX.Element | null = null;
  if (telegramLinked) {
    const date = formatLinkedAt(telegramLinkedAt);
    body = (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-gray-900">{date ? `Telegram подключён ${date}` : 'Telegram подключён'}</p>
        <Button variant="outline" size="sm" disabled={unlink.isPending} onClick={() => unlink.mutate()}>
          Отключить
        </Button>
      </div>
    );
  } else if (status && !status.connected) {
    body = <p className="text-sm text-gray-600">{MANAGER_BOT_NOT_CONNECTED_HINT}</p>;
  } else if (!isActive) {
    body = <p className="text-sm text-gray-600">{MANAGER_ARCHIVED_HINT}</p>;
  } else if (status?.connected) {
    body = linkUrl ? (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <code className="break-all rounded bg-gray-100 px-2 py-1 text-xs" data-testid="manager-telegram-link">
            {linkUrl}
          </code>
          <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
            Копировать
          </Button>
          {copied && <span className="text-xs text-green-700">Скопировано</span>}
        </div>
        <p className="text-xs text-gray-500">{MANAGER_LINK_HINT}</p>
      </div>
    ) : (
      <Button variant="outline" size="sm" disabled={createLink.isPending} onClick={() => createLink.mutate()}>
        Получить ссылку
      </Button>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-gray-200 p-4" data-testid="manager-telegram-block">
      <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <Send className="h-4 w-4 text-gray-400" />
        Telegram для уведомлений
      </h3>
      {body}
      {error && (
        <p className="text-xs text-red-700" data-testid="manager-telegram-error">
          {error}
        </p>
      )}
    </div>
  );
}
